/**
 * AFF 3D model layer — pure building blocks (#820).
 */

import { AIRCRAFT_TYPES, MODEL_COLOUR_FLYING, MODEL_COLOUR_LANDED } from './affSymbology';

/** Map-registered model id for an airframe code (RW, BD, TK, CL, FB). Unknown codes throw. */
export function modelIdFor(code: string): string {
  if (!AIRCRAFT_TYPES.some(t => t.code === code)) {
    throw new Error(`No 3D model for airframe code "${code}"`);
  }
  return `aff-model-${code.toLowerCase()}`;
}

/**
 * `model-rotation` for an aircraft: heading plus the model's forward-axis
 * offset, about the vertical axis only. A missing heading applies the offset
 * alone (no rotation); the provider is responsible for logging that gap.
 */
export function modelRotation(heading: number | null | undefined, offsetDeg: number): [number, number, number] {
  const turn = heading == null ? offsetDeg : heading + offsetDeg;
  return [0, 0, ((turn % 360) + 360) % 360];
}

const EARTH_CIRCUMFERENCE_M = 40075016.686;
const TILE_SIZE_PX = 512;

/**
 * Uniform `model-scale` factor that draws a model of `nativeLengthM` at
 * `targetPx` CSS pixels long, at the given zoom and latitude (Web Mercator).
 */
export function modelScaleAt(nativeLengthM: number, targetPx: number, zoom: number, latitudeDeg: number): number {
  const metresPerPixel = (EARTH_CIRCUMFERENCE_M * Math.cos((latitudeDeg * Math.PI) / 180)) / (TILE_SIZE_PX * 2 ** zoom);
  return (targetPx * metresPerPixel) / nativeLengthM;
}

/**
 * Height for `model-translation`: metres ABOVE THE TERRAIN.
 *
 * Measured against a height ladder in the live app: a GeoJSON model layer's
 * translation is 1:1 above the terrain — mapbox neither exaggerates it nor
 * counts it from sea level, whatever `model-elevation-reference` says. Terrain
 * is drawn exaggerated, so the aircraft's own altitude is exaggerated to match.
 *
 * A missing altitude sits the model on the terrain; the provider logs that gap.
 */
export function modelHeightAboveTerrain(
  altitudeAslM: number | null | undefined,
  exaggeration: number,
  groundRenderedM: number,
): number {
  if (altitudeAslM == null) return 0;
  return altitudeAslM * exaggeration - groundRenderedM;
}

/**
 * Metres to raise a model so its underside, not its origin, sits at the
 * aircraft's altitude. At constant screen size the model grows as zoom drops,
 * and without this its lower half sinks into the terrain.
 */
export function modelLift(scale: number, belowOriginM: number): number {
  return scale * belowOriginM;
}

/**
 * `model-rotation` paint value: [0, 0, rotDeg], built from the numeric `rotDeg`
 * property (see modelRotation) the same way as the translation.
 */
export const MODEL_ROTATION_EXPRESSION: unknown[] = [
  'interpolate', ['linear'], ['get', 'rotDeg'],
  0, ['literal', [0, 0, 0]],
  360, ['literal', [0, 0, 360]],
];

/** Highest translation the expression represents; above this mapbox clamps. */
const MAX_TRANSLATION_M = 100000;

/**
 * `model-translation` paint value: [0, 0, elev + lift]. Array properties are
 * stringified by GeoJSON sources, so the vector is built from the numeric
 * `elev` property by linear interpolation between literal endpoints.
 */
export function modelTranslationExpression(liftM: number): unknown[] {
  return [
    'interpolate', ['linear'], ['+', ['get', 'elev'], liftM],
    0, ['literal', [0, 0, 0]],
    MAX_TRANSLATION_M, ['literal', [0, 0, MAX_TRANSLATION_M]],
  ];
}

/**
 * `model-translation` with a PER-MODEL lift: [0, 0, elev + belowOrigin × scale].
 * Each feature carries its model's numeric `belowOrigin`, so a tanker (which
 * hangs further below its origin) is lifted more than a bird dog at the same scale.
 */
export function modelHeightExpression(scale: number): unknown[] {
  return [
    'interpolate', ['linear'], ['+', ['get', 'elev'], ['*', ['get', 'belowOrigin'], scale]],
    0, ['literal', [0, 0, 0]],
    MAX_TRANSLATION_M, ['literal', [0, 0, MAX_TRANSLATION_M]],
  ];
}

/**
 * `model-scale` paint value: the shared scale times each feature's numeric
 * `size` factor, built by linear interpolation to a literal vector the same way
 * as the rotation (array properties don't survive a GeoJSON source).
 */
export function modelScaleExpression(scale: number): unknown[] {
  return [
    'interpolate', ['linear'], ['get', 'size'],
    0, ['literal', [0, 0, 0]],
    1, ['literal', [scale, scale, scale]],
  ];
}

interface ModelLayerOptions {
  id: string;
  sourceId: string;
  /** Uniform model scale for the current zoom (the provider recomputes it on zoom). */
  scale: number;
}

/**
 * Layer spec for the AFF 3D models. Features carry numeric `elev`,
 * `belowOrigin`, `size` and `rotDeg`, string `modelId`, and `status` ('flying' | 'landed').
 */
export function buildModelLayer({ id, sourceId, scale }: ModelLayerOptions) {
  return {
    id,
    type: 'model',
    source: sourceId,
    layout: { 'model-id': ['get', 'modelId'] },
    paint: {
      'model-scale': modelScaleExpression(scale),
      'model-rotation': MODEL_ROTATION_EXPRESSION,
      'model-translation': modelHeightExpression(scale),
      // Heights are metres above the terrain (measured live); 'ground' says so.
      'model-elevation-reference': 'ground',
      'model-color': ['match', ['get', 'status'], 'landed', MODEL_COLOUR_LANDED, MODEL_COLOUR_FLYING],
      'model-color-mix-intensity': 1,
    },
  };
}
